/**
 * EntityHeader - Unified header for all entity types
 * Handles places (rating, price, hours) and events (calendar block, time)
 * Takes entityId, fetches its own data
 */

import { useState, type ReactNode } from "react";
import { Link } from "react-router-dom";
import {
  X,
  MapPin,
  Star,
  Share,
  Clock,
  AlarmClockCheck,
  AlarmClock,
  ImageOff,
} from "lucide-react";
import { OverlayIconButton, OverlayBadge, OVERLAY_ICON_SIZE } from "@/components/shared/OverlayIconButton";
import { OVERLAY_POSITION } from "@/components/shared/overlay-positions";
import { Pill } from "@/components/ui/Pill";
import { CAPTION, ROW_TEXT, ROW_TITLE, SECTION_TITLE } from "@/lib/typography";
import { cn } from "@/lib/utils";
import { useEntityCard } from "@/features/entity/useEntityDetail";
import { useIntersectionObserver } from "@/hooks/use-intersection-observer";
import { LeafActions } from "@/components/layout/LeafActions";
import { useEntityActions } from "@/features/entity/useEntityActions";
import { STUFF_DETAIL } from "@/routes";
import {
  entityPrimaryType,
  entityRating,
  entityReviewCount,
  extractOpeningHours,
  formatFieldName,
  formatPriceLevel,
  googlePlacesOf,
} from "@/features/entity/entitySummary";
import { getOpenStatus, formatOpenStatusText } from "@/utils/venueHoursUtils";
import { entityImageUrl } from "@/features/entity/entityImage";
import {
  getEventDisplayDates,
  getCalendarBlockParts,
  getEventRelativeDate,
  formatEventTimeRange,
  isSameDay,
} from "@/utils/eventDateUtils";
import { extractEntityLocation, formatDistance } from "@/utils/locationUtils";
import { isEventEntity, isPhysicalVenue } from "@/utils/entityTypeDetector";

interface EntityHeaderProps {
  entityId: string;
  /** Preserve an existing contextual detail route (for example, a collection witness). */
  href?: string;
  onClose?: () => void;
  onShare?: () => void;
  onHide?: () => void; // Called when user wants to hide/dismiss this entity
  className?: string;
  linkToEntity?: boolean; // If true, clicking header navigates to entity page
  imageFit?: "contain" | "cover"; // How to fit the image - contain shows full image, cover fills and crops
  /** "default" = standard h-64 card; "compact" = small horizontal card for carousels;
   *  "row" = thumbnail + name + one meta line, for entity lists on the stash page */
  variant?: "default" | "compact" | "row";
  /** Row variant only: extra control after the actions menu (e.g. an expand toggle) */
  trailing?: ReactNode;
  /** Shrink event calendar block + headline for tight spaces like condensed carousels */
  size?: "default" | "sm";
  /** Distance in km from user — shown in compact variant */
  distance?: number;
  /** Auth gate function from parent — triggers login nudge for anonymous users */
  requireAuth?: (actionType: string, targetId: string, callback: () => void) => void;
  /** Skip auth for API calls (public/anonymous access) */
  skipAuth?: boolean;
  /** Show reminder badge state on the card and eagerly load reminder counts */
  showReminderBadge?: boolean;
}

// Calendar block component for events
// Fixed size for consistency, content centers within
function CalendarBlock({
  startDate,
  endDate,
  className,
  size = "default",
}: {
  startDate: Date;
  endDate: Date | null;
  className?: string;
  size?: "default" | "sm";
}) {
  const start = getCalendarBlockParts(startDate);
  const hasRange = endDate && !isSameDay(startDate, endDate);
  const end = hasRange ? getCalendarBlockParts(endDate) : null;
  const sm = size === "sm";

  // Base styles, width varies by layout
  const baseClass = cn("flex flex-col items-center justify-center bg-background rounded-xl", sm ? "h-16" : "h-22");

  if (hasRange && end) {
    // Date range layout - wider to fit content
    return (
      <div className={cn(baseClass, sm ? "w-22" : "w-28", className)}>
        <div className={cn("flex items-center gap-1", sm ? "text-ui-badge" : "text-ui-caption")}>
          <span className="text-red-500 font-medium">{start.month}</span>
          <span className={cn("text-foreground font-bold leading-none", sm ? "text-ui-secondary-copy" : "text-ui-headline")}>{start.day}</span>
          <span className="text-muted-foreground">{start.weekday}</span>
        </div>
        <span className={cn("text-muted-foreground leading-none my-0.5", sm ? "text-ui-badge" : "text-ui-caption")}>–</span>
        <div className={cn("flex items-center gap-1", sm ? "text-ui-badge" : "text-ui-caption")}>
          <span className="text-red-500 font-medium">{end.month}</span>
          <span className={cn("text-foreground font-bold leading-none", sm ? "text-ui-secondary-copy" : "text-ui-headline")}>{end.day}</span>
          <span className="text-muted-foreground">{end.weekday}</span>
        </div>
      </div>
    );
  }

  // Single date layout
  return (
    <div className={cn(baseClass, sm ? "w-16" : "w-22", className)}>
      <span className={cn("text-red-500 font-semibold tracking-wider leading-none", sm ? "text-ui-badge-small" : "text-ui-micro")}>
        {start.month}
      </span>
      <span className={cn("text-foreground font-bold leading-none", sm ? "text-ui-headline" : "text-ui-title")}>{start.day}</span>
      <span
        className={cn(
          "text-muted-foreground font-medium tracking-wider leading-none",
          sm ? "text-ui-badge-small" : "text-ui-micro",
        )}
      >
        {start.weekday}
      </span>
    </div>
  );
}

// Types that already have dedicated section headers in StashDetail — suppress redundant pills
const SECTION_HEADER_TYPES = new Set(["book", "recipe", "product"]);

export function EntityHeader({
  entityId,
  href,
  onClose,
  onShare,
  onHide,
  className,
  linkToEntity = false,
  imageFit = "contain",
  variant = "default",
  size = "default",
  distance,
  requireAuth,
  skipAuth = false,
  showReminderBadge = true,
  trailing,
}: EntityHeaderProps) {
  const { elementRef: visibilityRef, hasIntersected } = useIntersectionObserver<HTMLDivElement>({
    rootMargin: "200px",
    threshold: 0.15,
    triggerOnce: true,
  });
  const { data, isLoading } = useEntityCard(entityId, skipAuth);
  const [failedImageFor, setFailedImageFor] = useState<string | null>(null);
  const imageError = failedImageFor === entityId;

  const entity = data?.entity;

  // Use explicit distance prop, fall back to entity data
  const resolvedDistance = distance ?? entity?.distance_km;

  const imageUrl = entityImageUrl(entity);
  const name = entity?.name || entity?.entity_name || "Untitled";
  const gp = googlePlacesOf(entity);

  // Extract location string from entity
  const location = extractEntityLocation(entity);

  // Extract venue-style metadata (fallback to top-level fields from EntitySummary)
  const priceLevel = gp?.price_level ?? gp?.priceLevel;
  const primaryType = entityPrimaryType(entity);
  const firstTag = Array.isArray(entity?.tags) ? entity.tags[0] : undefined;
  const openingHours = extractOpeningHours(entity);
  const ratingNumber = entityRating(entity);
  const reviewCountNumber = entityReviewCount(entity);

  const formattedPrice = formatPriceLevel(priceLevel);
  const typeDisplay =
    primaryType && !SECTION_HEADER_TYPES.has(primaryType.toLowerCase()) ? formatFieldName(primaryType) : undefined;
  const tagDisplay = firstTag ? formatFieldName(firstTag) : undefined;

  // Event-specific data
  const isEvent = isEventEntity(entity);
  const eventDates = isEvent ? getEventDisplayDates(entity) : null;
  const eventTimeDisplay = isEvent ? formatEventTimeRange(entity) : null;
  const eventRelativeDate = eventDates ? getEventRelativeDate(entity) : null;
  const { actions, panels, setMenuOpen, reminderCount, openReminders } = useEntityActions({
    entityId, entity, onShare, onHide, requireAuth,
    loadReminderStatus: showReminderBadge && hasIntersected,
  });


  if (isLoading) {
    return null;
  }

  if (!entity) return null;

  // Get detailed open status
  const openStatus =
    openingHours?.lines && openingHours.lines.length > 0 ? getOpenStatus(openingHours.lines, entity.timezone) : null;
  const openStatusText = openStatus ? formatOpenStatusText(openStatus, entity.timezone) : null;

  const splitStatusText = (text: string) => {
    if (text.includes("until")) {
      const parts = text.split(" until ");
      return { main: parts[0], details: parts[1] ? ` until ${parts[1]}` : "" };
    }
    const words = text.split(" ");
    return { main: words[0], details: words.slice(1).join(" ") ? ` ${words.slice(1).join(" ")}` : "" };
  };

  const { main: statusMain, details: statusDetails } = openStatusText
    ? splitStatusText(openStatusText)
    : { main: "", details: "" };

  // Determine if content will be on a dark overlay (image) or theme-aware background (no image)
  const onOverlay = Boolean(imageUrl) && !imageError;

  // Only show venue-specific metadata (location, rating, price, hours) for physical venues
  const isVenue = isPhysicalVenue(entity);

  // Workout-specific pills (replaces generic category labels)
  const isWorkout = entity?.routing_class?.toLowerCase() === "workout";
  const workoutPills: string[] = isWorkout
    ? [
        entity?.activity_type,
        entity?.duration,
        entity?.difficulty,
        ...(entity?.target_areas?.slice(0, 2) ?? []),
      ].filter((v): v is string => Boolean(v))
    : [];

  // Shared info content (used in both layouts)
  const infoContent = (
    <div className="space-y-1.5">
      {/* Open status pill */}
      {openStatusText && (
        <div className="flex items-center">
          <span
            className={cn(
              "text-ui-caption px-2 py-1 rounded-full",
              openStatus?.isOpen ? "bg-muted" : "bg-muted",
            )}
          >
            <span
              className={cn(
                "font-medium",
                openStatus?.isOpen ? "text-positive" : "text-destructive",
              )}
            >
              {statusMain}
            </span>
            {statusDetails && <span className={cn(CAPTION, "font-normal")}>{statusDetails}</span>}
          </span>
        </div>
      )}

      {/* Name */}
      <h1
        className={cn(SECTION_TITLE, "truncate mb-0.5", onOverlay ? "text-white" : "text-foreground")}
      >
        {name}
      </h1>

      {/* Rating + Price */}
      <div className={cn(ROW_TEXT, "flex items-center gap-3 overflow-hidden")}>
        {ratingNumber !== undefined && (
          <div className="flex items-center gap-1">
            <Star className="h-4 w-4 fill-yellow-400 text-yellow-400" />
            <span className={cn("font-medium", onOverlay ? "text-white" : "text-foreground")}>
              {ratingNumber.toFixed(1)}
            </span>
            {reviewCountNumber !== undefined && (
              <span className={onOverlay ? "text-white/80" : "text-muted-foreground"}>
                ({reviewCountNumber.toLocaleString()})
              </span>
            )}
          </div>
        )}

        {resolvedDistance !== undefined && (
          <span className={cn("flex-shrink-0", onOverlay ? "text-white/80" : "text-muted-foreground")}>
            {formatDistance(resolvedDistance)}
          </span>
        )}

        {formattedPrice && (
          <div className="flex items-center">
            {formattedPrice.display === "free" ? (
              <Pill
                variant={onOverlay ? "overlay" : "muted"}
                className={!onOverlay ? "bg-secondary text-secondary-foreground" : undefined}
              >
                Free
              </Pill>
            ) : (
              <div className="flex items-center">
                {[1, 2, 3, 4].map(num => (
                  <span
                    key={num}
                    className={cn(
                      ROW_TEXT,
                      num <= formattedPrice.level
                        ? onOverlay
                          ? "text-white"
                          : "text-foreground"
                        : onOverlay
                          ? "text-white/30"
                          : "text-muted-foreground/30",
                    )}
                  >
                    $
                  </span>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Category / Workout meta */}
      {isWorkout ? (
        workoutPills.length > 0 && (
          <div className="flex items-center gap-1.5 overflow-hidden flex-wrap">
            {workoutPills.map((pill, i) => (
              <Pill key={i} variant={onOverlay ? "overlay" : "outlined"} className="truncate capitalize">
                {pill}
              </Pill>
            ))}
          </div>
        )
      ) : typeDisplay || tagDisplay ? (
        <div className="flex items-center gap-1.5 overflow-hidden">
          {typeDisplay && (
            <Pill variant={onOverlay ? "overlay" : "outlined"} className="truncate">
              {typeDisplay}
            </Pill>
          )}
          {tagDisplay && (
            <Pill variant={onOverlay ? "overlay" : "outlined"} className="truncate">
              {tagDisplay}
            </Pill>
          )}
        </div>
      ) : null}

      {/* Location — venues only */}
      {isVenue && location && (
        <div
          className={cn(
            ROW_TEXT,
            "flex items-center gap-1 min-w-0",
            onOverlay ? "text-white/80" : "text-muted-foreground",
          )}
        >
          <MapPin className="w-3.5 h-3.5 flex-shrink-0" />
          <span className="truncate">{location}</span>
        </div>
      )}
    </div>
  );

  // Event info content with calendar block at top-left
  const isSm = size === "sm";
  // Calendar block overlay — rendered at top-left of the card (outside bottom content)
  const calendarOverlay =
    isEvent && eventDates?.start ? (
      <div className={cn("absolute z-10", isSm ? "top-2 left-2" : "top-4 left-4")}>
        <CalendarBlock startDate={eventDates.start} endDate={eventDates.end} size={size} />
      </div>
    ) : null;

  const eventInfoContent = eventDates?.start ? (
    <div className={isSm ? "space-y-1" : "space-y-2"}>
      <h1
        className={cn(
          "truncate",
          isSm ? ROW_TITLE : SECTION_TITLE,
          onOverlay ? "text-white" : "text-foreground",
        )}
      >
        {name}
      </h1>

      {/* Type + All day badges */}
      {!isSm && (typeDisplay || eventDates.allDay) && (
        <div className="flex items-center gap-1.5 min-w-0 overflow-hidden">
          {typeDisplay && (
            <Pill variant={onOverlay ? "overlay" : "outlined"} className="truncate">
              {typeDisplay}
            </Pill>
          )}
          {eventDates.allDay && (
            <Pill variant={onOverlay ? "overlay" : "outlined"} className="flex-shrink-0">
              All day
            </Pill>
          )}
        </div>
      )}

      {/* Time + Location on same line */}
      {(eventRelativeDate || eventTimeDisplay || location) && (
        <div
          className={cn(
            "flex items-center gap-1.5 min-w-0",
            isSm ? "text-ui-caption" : "text-ui-control",
            onOverlay ? "text-white/80" : "text-muted-foreground",
          )}
        >
          {(eventRelativeDate || eventTimeDisplay) && (
            <>
              <Clock className={cn("flex-shrink-0", isSm ? "w-3 h-3" : "w-3.5 h-3.5")} />
              <span className="flex-shrink-0">
                {eventTimeDisplay && !eventDates.allDay ? (
                  <>
                    {getEventRelativeDate(entity, false)} · {eventTimeDisplay}
                  </>
                ) : (
                  eventRelativeDate
                )}
              </span>
            </>
          )}
          {(eventRelativeDate || eventTimeDisplay) && location && <span className="flex-shrink-0">·</span>}
          {location && (
            <>
              <MapPin className={cn("flex-shrink-0", isSm ? "w-3 h-3" : "w-3.5 h-3.5")} />
              <span className="truncate">{location}</span>
            </>
          )}
        </div>
      )}
    </div>
  ) : null;

  // Action buttons positioned relative to the entire header (not inside image)
  const actionButtons = (
      <div className={cn(OVERLAY_POSITION.topRight, "flex items-center gap-2")}>
        {showReminderBadge && reminderCount > 0 && (
          <OverlayIconButton
            onClick={e => {
              e.preventDefault();
              e.stopPropagation();
              openReminders();
            }}
            aria-label="Reminders"
            className="relative"
          >
            {reminderCount > 0 ? (
              <AlarmClockCheck className={OVERLAY_ICON_SIZE} />
            ) : (
              <AlarmClock className={OVERLAY_ICON_SIZE} />
            )}
            <OverlayBadge count={reminderCount} />
          </OverlayIconButton>
        )}
        <LeafActions page="stuff" actions={actions} presentation="menu" onMenuOpenChange={setMenuOpen} onOverlay={onOverlay} />
        {onShare && (
          <OverlayIconButton
            onClick={e => {
              e.preventDefault();
              e.stopPropagation();
              onShare();
            }}
            aria-label="Share"
          >
            <Share className={OVERLAY_ICON_SIZE} />
          </OverlayIconButton>
        )}
        {onClose && (
          <OverlayIconButton
            onClick={e => {
              e.preventDefault();
              onClose();
            }}
            aria-label="Close"
          >
            <X className={OVERLAY_ICON_SIZE} />
          </OverlayIconButton>
        )}
      </div>
  );

  // Select the appropriate info content based on entity type
  const displayContent = isEvent && eventInfoContent ? eventInfoContent : infoContent;

  // No image layout - same height as image cards, content at bottom
  const cardHeight = isSm ? "h-full" : "h-64";
  const cardPadding = isSm ? "p-2.5" : "p-4";
  const noImageContent = (
    <div ref={visibilityRef} className={cn("relative isolate", linkToEntity && "cursor-pointer")}>
      {actionButtons}
      {calendarOverlay}
      <div className={cn(cardHeight, "flex flex-col justify-end rounded-lg border border-border", cardPadding)}>
        {displayContent}
      </div>
    </div>
  );

  // Image layout - contain ensures full image visible, black bg fills gaps
  const imageContent = (
    <div ref={visibilityRef} className={cn("relative isolate", linkToEntity && "cursor-pointer")}>
      {actionButtons}
      {calendarOverlay}
      <div className={cn("relative rounded-lg bg-black overflow-hidden", cardHeight)}>
        <img
          src={imageUrl!}
          alt={name}
          className={cn("w-full h-full", imageFit === "cover" ? "object-cover" : "object-contain")}
          onError={() => setFailedImageFor(entityId)}
          loading="lazy"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
        <div className={cn("absolute bottom-0 left-0 right-0", cardPadding)}>{displayContent}</div>
      </div>
    </div>
  );

  // -- Compact variant: mini card for condensed carousels --------------------
  if (variant === "compact") {
    const compactContent = (
      <div
        ref={visibilityRef}
        className={cn("relative isolate h-full overflow-hidden rounded-lg", onOverlay ? "bg-black" : "bg-muted")}
      >
        {imageUrl && !imageError ? (
          <img
            src={imageUrl}
            alt={name}
            className="w-full h-full object-cover"
            onError={() => setFailedImageFor(entityId)}
            loading="lazy"
          />
        ) : null}
        <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/40 to-transparent" />

        {/* Action buttons — compact uses tighter inset than default cards */}
        <div className="absolute top-1.5 right-1.5 z-10 flex items-center gap-1">
          {showReminderBadge && reminderCount > 0 && (
            <OverlayIconButton
              onClick={e => {
                e.preventDefault();
                e.stopPropagation();
                openReminders();
              }}
              aria-label="Reminders"
              className="relative"
            >
              <AlarmClockCheck className={OVERLAY_ICON_SIZE} />
              <OverlayBadge count={reminderCount} />
            </OverlayIconButton>
          )}
          <LeafActions page="stuff" actions={actions} presentation="menu" onMenuOpenChange={setMenuOpen} onOverlay={onOverlay} />
        </div>

        {/* Calendar block for events — top left */}
        {isEvent && eventDates?.start && (
          <div className="absolute top-2 left-2 z-10">
            <CalendarBlock startDate={eventDates.start} endDate={eventDates.end} size="sm" />
          </div>
        )}

        <div className="absolute bottom-0 left-0 right-0 p-2 space-y-1">
          {/* Open status pill */}
          {openStatusText && (
            <span
              className={cn(
                "inline-block px-1.5 py-0.5 rounded-full text-ui-badge leading-tight",
                openStatus?.isOpen ? "bg-muted" : "bg-muted",
              )}
            >
              <span
                className={cn(
                  "font-medium",
                  openStatus?.isOpen ? "text-positive" : "text-destructive",
                )}
              >
                {statusMain}
              </span>
              {statusDetails && <span className="font-normal text-muted-foreground">{statusDetails}</span>}
            </span>
          )}
          {/* Name */}
          <p className={cn(ROW_TITLE, "truncate text-white")}>{name}</p>
          {/* Meta row */}
          <div className={cn(CAPTION, "flex items-center gap-1.5 text-white/80 overflow-hidden")}>
            {ratingNumber !== undefined && (
              <span className="flex items-center gap-0.5 flex-shrink-0">
                <Star className="h-3 w-3 fill-yellow-400 text-yellow-400" />
                <span className="font-medium text-white">{ratingNumber.toFixed(1)}</span>
              </span>
            )}
            {resolvedDistance !== undefined && (
              <>
                {ratingNumber !== undefined && <span>·</span>}
                <span className="flex-shrink-0">{formatDistance(resolvedDistance)}</span>
              </>
            )}
            {isEvent && eventRelativeDate ? (
              <>
                {(ratingNumber !== undefined || resolvedDistance !== undefined) && <span>·</span>}
                <Clock className="h-3 w-3 flex-shrink-0" />
                <span className="flex-shrink-0">{eventRelativeDate}</span>
              </>
            ) : isWorkout && workoutPills[0] ? (
              <>
                {(ratingNumber !== undefined || resolvedDistance !== undefined) && <span>·</span>}
                <span className="flex-shrink-0 capitalize">{workoutPills[0]}</span>
              </>
            ) : typeDisplay ? (
              <>
                {(ratingNumber !== undefined || resolvedDistance !== undefined) && <span>·</span>}
                <span className="flex-shrink-0">{typeDisplay}</span>
              </>
            ) : null}
          </div>
        </div>
      </div>
    );

    if (linkToEntity) {
      return (
        <>
          <Link to={href ?? STUFF_DETAIL({ entityId })} className={cn("block h-full", className)}>
            {compactContent}
          </Link>
          {panels}
        </>
      );
    }
    return (
      <>
        <div className={cn("h-full", className)}>{compactContent}</div>
        {panels}
      </>
    );
  }

  // -- Default variant -------------------------------------------------------
  const content = imageUrl && !imageError ? imageContent : noImageContent;

  // -- Row variant: list row for the stash page's entity sections ------------
  if (variant === "row") {
    const eventWhen = eventDates?.start
      ? eventTimeDisplay && !eventDates.allDay
        ? `${getEventRelativeDate(entity, false)} · ${eventTimeDisplay}`
        : eventRelativeDate
      : null;
    const kind = isWorkout ? workoutPills.slice(0, 2).join(" · ") : typeDisplay;
    const address = isVenue || isEvent ? location : null;

    const rowBody = (
      <div className="flex min-w-0 flex-1 items-center gap-3 py-3.5">
        {isEvent && eventDates?.start ? (
          <CalendarBlock
            startDate={eventDates.start}
            endDate={eventDates.end}
            size="sm"
            className={cn(
              "h-14 flex-shrink-0 bg-muted",
              // Single-day tiles match the 56px thumbnails; ranges keep their width.
              !(eventDates.end && !isSameDay(eventDates.start, eventDates.end)) && "w-14",
            )}
          />
        ) : (
          <div className="flex h-14 w-14 flex-shrink-0 items-center justify-center overflow-hidden rounded-[9px] bg-muted">
            {imageUrl && !imageError ? (
              <img
                src={imageUrl}
                alt=""
                className="h-full w-full object-cover"
                onError={() => setFailedImageFor(entityId)}
                loading="lazy"
              />
            ) : (
              <ImageOff className="h-[18px] w-[18px] text-muted-foreground/60" />
            )}
          </div>
        )}
        <div className="flex min-w-0 flex-1 flex-col gap-1">
          <p className={cn("line-clamp-2 text-foreground", ROW_TITLE)}>{name}</p>
          {(eventWhen || kind || ratingNumber !== undefined || openStatusText) && (
            <div className={cn(CAPTION, "flex min-w-0 items-center gap-2")}>
              {(eventWhen || kind) && <span className="truncate">{eventWhen || kind}</span>}
              {ratingNumber !== undefined && (
                <span className="flex flex-shrink-0 items-center gap-0.5">
                  <Star className="h-3 w-3 fill-yellow-400 text-yellow-400" />
                  {ratingNumber.toFixed(1)}
                </span>
              )}
              {openStatusText && (
                <span className="truncate">
                  <span className={openStatus?.isOpen ? "text-positive" : "text-destructive"}>
                    {statusMain}
                  </span>
                  {statusDetails}
                </span>
              )}
            </div>
          )}
          {address && <p className={cn(CAPTION, "truncate")}>{address}</p>}
        </div>
      </div>
    );

    return (
      <>
        <div ref={visibilityRef} className={cn("flex min-w-0 items-center gap-1 border-b border-border", className)}>
          {linkToEntity ? (
            <Link to={href ?? STUFF_DETAIL({ entityId })} className="flex min-w-0 flex-1 transition-opacity hover:opacity-80">
              {rowBody}
            </Link>
          ) : (
            rowBody
          )}
          {showReminderBadge && reminderCount > 0 && (
            <button
              type="button"
              onClick={openReminders}
              aria-label="Reminders"
              className="flex-shrink-0 rounded-full p-1.5 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
            >
              <AlarmClockCheck className="h-4 w-4" />
            </button>
          )}
          <LeafActions page="stuff" actions={actions} presentation="menu" onMenuOpenChange={setMenuOpen} onOverlay={false} />
          {trailing}
        </div>
        {panels}
      </>
    );
  }

  if (linkToEntity) {
    return (
      <>
        <Link to={href ?? STUFF_DETAIL({ entityId })} className={cn("block", className)}>
          {content}
        </Link>
        {panels}
      </>
    );
  }

  return (
    <>
      <div className={className}>{content}</div>
      {panels}
    </>
  );
}
